import { useEffect, useMemo, useRef, useState } from "react";

import { EReputationCategory, EReputationStatus } from "src/core/constants/eReputation";
import { MappedStakeholder } from "src/core/constants/folder";
import { AdvancedPersonSearchQuery, useAdvancedPersonSearchLazyQuery } from "src/core/gql/graphql";
import { useStakeholderDuplicatesActions } from "src/core/hooks/folder/stakeholder-duplicates/useStakeholderDuplicatesActions";
import { mapReputationStatus } from "src/core/mappers/entity.mapper";

type AdvancedPerson = NonNullable<
  NonNullable<AdvancedPersonSearchQuery["advancedPersonSearch"]>["persons"]
>[number];

type AdvancedPersonWithEReputationStatus = AdvancedPerson & {
  pepStatus: EReputationStatus;
  sanctionStatus: EReputationStatus;
};

type UseStakeholdersDuplicateAutoCheckParams = {
  folderId?: string;
  stakeholders: MappedStakeholder[];
  enabled?: boolean;
};

export type StakeholdersDuplicateAutoCheckResult = {
  /**
   * Override du champ duplicateCheckDone (uniquement côté front).
   * - true  => pas de doublon (persons <= 1)
   * - false => doublon trouvé (persons > 1) => on affiche le bouton pour ouvrir la modale
   */
  duplicateCheckAutoByStakeholderId: Record<string, boolean>;
  /** Liste des candidats retournés par la recherche, utilisée par la modale. */
  duplicateCandidatesByStakeholderId: Record<string, AdvancedPersonWithEReputationStatus[]>;
  duplicateCheckLoadingByStakeholderId: Record<string, boolean>;
  /**
   * Indique qu'au moins une réponse est revenue pour ce stakeholder
   * (succès ou erreur).
   */
  duplicateCheckFetchedByStakeholderId: Record<string, boolean>;
};

const buildFingerprint = (stakeholder: MappedStakeholder) => {
  const firstName = stakeholder.firstName?.trim() ?? "";
  const lastName = stakeholder.lastName?.trim() ?? "";
  const birthDate = stakeholder.birthDate?.trim() ?? "";
  return `${stakeholder.id}|${firstName}|${lastName}|${birthDate}`;
};

const countFilledIdentityFields = (stakeholder: MappedStakeholder) => {
  const firstName = stakeholder.firstName?.trim() ?? "";
  const lastName = stakeholder.lastName?.trim() ?? "";
  const birthDate = stakeholder.birthDate?.trim() ?? "";
  return [firstName, lastName, birthDate].filter(Boolean).length;
};

const mapAdvancedPersonWithEReputationStatus = (
  person: AdvancedPerson
): AdvancedPersonWithEReputationStatus => {
  const eReputation = person.eReputation;

  const pepEntry = eReputation?.entries.find(
    (entry) => entry.category.code === EReputationCategory.PEP
  );
  const sanctionEntry = eReputation?.entries.find(
    (entry) => entry.category.code === EReputationCategory.SANCTION
  );

  const pepStatus = mapReputationStatus(pepEntry);
  const sanctionStatus = mapReputationStatus(sanctionEntry);

  return {
    ...person,
    pepStatus,
    sanctionStatus,
  };
};

/**
 * Ce hook gère UNIQUEMENT la vérification automatique des doublons des stakeholders (PPH).
 *
 * Responsabilités :
 * - déterminer quels stakeholders doivent être vérifiés
 * - déclencher la requête GraphQL advancedPersonSearch
 * - mémoriser : loading / candidats / override duplicateCheckDone / fingerprint
 * - reset propre à chaque changement de dossier
 */
export const useStakeholdersDuplicateAutoCheck = ({
  folderId,
  stakeholders,
  enabled = true,
}: UseStakeholdersDuplicateAutoCheckParams): StakeholdersDuplicateAutoCheckResult => {
  const [advancedPersonSearch] = useAdvancedPersonSearchLazyQuery({
    fetchPolicy: "no-cache",
  });
  const { updateDuplicateCheckDone } = useStakeholderDuplicatesActions();

  const [duplicateCheckAutoByStakeholderId, setDuplicateCheckAutoByStakeholderId] = useState<
    Record<string, boolean>
  >({});
  const [duplicateCandidatesByStakeholderId, setDuplicateCandidatesByStakeholderId] = useState<
    Record<string, AdvancedPersonWithEReputationStatus[]>
  >({});
  const [duplicateCheckLoadingByStakeholderId, setDuplicateCheckLoadingByStakeholderId] = useState<
    Record<string, boolean>
  >({});
  const [duplicateCheckFetchedByStakeholderId, setDuplicateCheckFetchedByStakeholderId] = useState<
    Record<string, boolean>
  >({});

  // Mémoire des paramètres déjà checkés, pour éviter les appels inutiles
  const fingerprintByStakeholderIdRef = useRef<Record<string, string>>({});

  // Reset des infos de duplication quand on change de dossier
  useEffect(() => {
    setDuplicateCheckAutoByStakeholderId({});
    setDuplicateCandidatesByStakeholderId({});
    setDuplicateCheckLoadingByStakeholderId({});
    setDuplicateCheckFetchedByStakeholderId({});
    fingerprintByStakeholderIdRef.current = {};
  }, [folderId, enabled]);

  const stakeholdersToCheck = useMemo(() => {
    if (!enabled) return [];

    return stakeholders.filter((s) => {
      // On check uniquement les personnes (pas l'entreprise du dossier)
      if (s.isFolderCustomer) return false;

      // Si la vérif a déjà été faite (côté back ou via customBoolean) => ne pas refaire
      if (s.duplicateCheckDone) return false;

      // On ne lance pas de recherche si moins de 2 champs sur 3 sont présents
      if (countFilledIdentityFields(s) < 2) return false;

      // Si on a déjà check exactement ces valeurs, on skip
      const fingerprint = buildFingerprint(s);
      if (fingerprintByStakeholderIdRef.current[s.id] === fingerprint) return false;
      return true;
    });
  }, [enabled, stakeholders]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    /**
     * Méthode permettant de vérifier les doublons pour les stakeholders.
     */
    const run = async () => {
      for (const stakeholder of stakeholdersToCheck) {
        const firstName = stakeholder.firstName?.trim() ?? "";
        const lastName = stakeholder.lastName?.trim() ?? "";
        const birthDate = stakeholder.birthDate?.trim() ?? "";

        if (!cancelled) {
          setDuplicateCheckLoadingByStakeholderId((prev) => ({ ...prev, [stakeholder.id]: true }));
        }

        const fingerprint = buildFingerprint(stakeholder);

        try {
          const res = await advancedPersonSearch({
            variables: {
              values: [firstName, lastName, birthDate],
              limit: 50,
            },
          });

          const persons = res.data?.advancedPersonSearch?.persons ?? [];

          // On exclut le stakeholder lui-même des candidats (afin d'éviter d'avoir un doublon dans la modale).
          const filteredPersons = persons.filter(
            (person) => !(person.isPerson && person.isCustomer) && person.id !== stakeholder.id
          );
          const personsWithEReputationStatus = filteredPersons.map(
            mapAdvancedPersonWithEReputationStatus
          );
          const personsCount = filteredPersons.length;

          // - persons <= 1 => pas de doublon trouvé => duplicateCheckDone=true
          // - persons > 1 => doublon trouvé => duplicateCheckDone=false (=> bouton + modale)
          const duplicateCheckDone = personsCount < 1;

          if (duplicateCheckDone) {
            try {
              await updateDuplicateCheckDone({
                personId: stakeholder.id,
                duplicateCheckDone: "true",
              });
            } catch (error) {
              console.error(
                "[useStakeholdersDuplicateAutoCheck] updateDuplicateCheckDone failed for stakeholder",
                stakeholder.id,
                error
              );
            }
          }

          if (!cancelled) {
            setDuplicateCheckAutoByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: duplicateCheckDone,
            }));

            // On stocke les candidats (utile surtout si personsCount > 1)
            setDuplicateCandidatesByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: personsWithEReputationStatus,
            }));

            // On indique que la vérification a été faite pour ce stakeholder
            setDuplicateCheckFetchedByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: true,
            }));

            fingerprintByStakeholderIdRef.current[stakeholder.id] = fingerprint;
          }
        } catch (error) {
          // En cas d'erreur, on laisse l'état "non vérifié" (duplicateCheckDone=false),
          // et pas de candidats.
          if (!cancelled) {
            setDuplicateCheckAutoByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: false,
            }));
            setDuplicateCandidatesByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: [],
            }));
            setDuplicateCheckFetchedByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: true,
            }));
            fingerprintByStakeholderIdRef.current[stakeholder.id] = fingerprint;
          }

          console.error(
            "[useStakeholdersDuplicateAutoCheck] advancedPersonSearch failed for stakeholder",
            stakeholder.id,
            error
          );
        } finally {
          if (!cancelled) {
            setDuplicateCheckLoadingByStakeholderId((prev) => ({
              ...prev,
              [stakeholder.id]: false,
            }));
          }
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [advancedPersonSearch, enabled, stakeholdersToCheck, updateDuplicateCheckDone]);

  return {
    duplicateCheckAutoByStakeholderId,
    duplicateCandidatesByStakeholderId,
    duplicateCheckLoadingByStakeholderId,
    duplicateCheckFetchedByStakeholderId,
  };
};
